import { useState, type FormEvent } from 'react'
import './LoginPage.css'
import { BP_BRANDING_VERSION } from './lib/branding/brand'

// JWM auth: a simple email + password login backed by the D1 `users` table and
// server-side sessions (replaces the inherited Cloudflare Access / Google SSO).
// On success the server sets an HttpOnly `jwm_session` cookie; we reload so
// <Root/>'s /api/me check picks up the authenticated session.
export function LoginPage({ errorMessage }: { errorMessage?: string | null } = {}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(() => {
    if (errorMessage) return errorMessage
    try {
      const p = new URLSearchParams(window.location.search).get('login')
      if (p === 'google_denied') return 'That Google account isn’t allowed to sign in here.'
      if (p && p.startsWith('google')) return 'Google sign-in didn’t complete. Please try again.'
    } catch { /* ignore */ }
    return null
  })
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<'google' | 'credentials'>('google')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      })
      const data = await r.json().catch(() => ({} as Record<string, unknown>))
      if (r.ok && (data as { ok?: boolean }).ok) {
        window.location.reload()
        return
      }
      setErr((data as { error?: string }).error || 'Invalid email or password.')
    } catch {
      setErr('Could not reach the server. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    padding: '12px 14px',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.18)',
    background: '#f8fafc',
    color: '#171717',
    fontSize: 15,
    width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <div className="login-root" data-branding={BP_BRANDING_VERSION} data-marker="jwm-login-v1">
      <div className="login-content">
        <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '0.1em', color: '#171717', margin: '2px 0 4px' }}>JWM</div>
        {err && (
          <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.35)', color: '#b91c1c', fontSize: 14, lineHeight: 1.5, textAlign: 'center', width: 320, maxWidth: '90vw', margin: '10px auto 0', boxSizing: 'border-box' }}>
            {err}
          </div>
        )}
        {mode === 'google' ? (
          <>
            <button type="button" onClick={() => { window.location.href = '/api/auth/google/start' }} className="login-btn" style={{ width: 320, maxWidth: '90vw', margin: '10px auto 0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.6 5.6C41.6 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
              <span>Sign in with Google</span>
            </button>
            <button type="button" onClick={() => { setErr(null); setMode('credentials') }} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 0, color: '#475569', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>Use credentials</button>
          </>
        ) : (
          <>
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, width: 320, maxWidth: '90vw', margin: '10px auto 0' }}>
              <input type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus style={inputStyle} />
              <input type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
              <button type="submit" className="login-btn" disabled={busy}>
                <span>{busy ? 'Signing in…' : 'Sign in'}</span>
              </button>
            </form>
            <button type="button" onClick={() => { setErr(null); setMode('google') }} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 0, color: '#475569', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>← Sign in with Google</button>
          </>
        )}
      </div>
      <div className="login-hero" aria-hidden="true">
        <img src="/jwm-logo.png?v=jwm3" alt="JWM" />
      </div>
    </div>
  )
}
